/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React from 'react'
import _ from 'lodash'
import { AdvButton } from '@components/Button'
import { WriteMessageIco } from '@shared/Icon/MessageIcon'
import PopupUtils from '@utils/popup-utils'
import { PAGE_TYPE } from '@interface/common'
import DialogUtils from '@utils/dialog-utils'
import useRootStore from '@store/useRootStore'
import Login from '@views/Login'
import { FROM_VIEW_TYPE, USER_PENALTY_TYPE } from '@v2/constants/common'
import * as Utils from '@utils/utils'
import { TOAST_TYPE } from '@utils/utils'
import { UserPickPromotionBanner } from '@views/Home/UserPick/Components/UserPickPromotionBanner'
import styled from '@themes/theme'
import { FloatingBottom, FloatingBottomNoNav, SafeAreaConstantPadding, SafeAreaEnvPadding } from '@styles/base.style'
import StorageUtils from '@utils/storage-utils'

interface userPickWriteBtnProps {
  float?: boolean
  fixed?: boolean
  sticky?: boolean
}

const floatStyle = css`
  float: right;
  margin: 18px 18px 0 0;
`
const fixedStyle = css`
  position: fixed;
  bottom: ${FloatingBottom};
  right: 14px;
  width: auto;
  height: auto;
  //min-height: 54px;
  padding-bottom: ${SafeAreaEnvPadding};
  padding-bottom: ${SafeAreaConstantPadding};

  // 하단 네비게이션이 없는 화면
  &.no-nav {
    bottom: ${FloatingBottomNoNav};
  }
`
const stickyStyle = css`
  position: sticky;
  bottom: ${FloatingBottomNoNav};
  right: 14px;
  float: right;
  width: auto;
  height: auto;
  min-height: 54px;
`
const UserPickWriteBtn = styled.div<userPickWriteBtnProps>`
  position: relative;

  ${props => props.float && floatStyle}
  ${props => props.fixed && fixedStyle}
  ${props => props.sticky && stickyStyle}
  button {
    &.write-btn {
      background: ${props => props.theme.color.mainBg};
      box-shadow: 0 3px 9px ${props => props.theme.color.mainBg};

      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: inherit;
        border-radius: 50%;
        border: 1px solid ${props => props.theme.color.btn};
        opacity: .5;
      }
    }

    svg {
      .fill {
        fill: ${props => props.theme.color.underover};
      }
    }
  }

  &.pc-filter-write-btn {
    bottom: 41px;
  }
`

export interface UserPickWriteButtonProps {
  className?: string
  float?: boolean
  fixed?: boolean
  sticky?: boolean
  pageType?: PAGE_TYPE
  fromView: FROM_VIEW_TYPE
  showBanner?: boolean
}

export const UserPickWriteButton: React.FC<UserPickWriteButtonProps> = observer((props): React.ReactElement => {
  const { bettingHistoryStore, authStore, userPickStore, penaltyStore, logStore } = useRootStore()
  const { onGoingBetCount } = bettingHistoryStore
  const { selectedUserPickMatch } = userPickStore
  const { className, float, fixed, sticky, pageType, fromView, showBanner } = props
  const onClickOpenEditor = () => {
    if (bettingHistoryStore.selectedBettingHistoryFoldData) {
      PopupUtils.showUserPickEditor(fromView)
    }
  }

  const showBettingHistory = () => {
    StorageUtils.saveShowUserPickFirstInfo()
    let bettingHistoryPageType = PAGE_TYPE.USER_PICK
    if (pageType) {
      bettingHistoryPageType = pageType
    }
    bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.USER_PICK)
    bettingHistoryStore.setBettingHistoryInitProps({ onlyMajorClub: true, fromViewType: FROM_VIEW_TYPE.USER_PICK })
    PopupUtils.showBettingHistory({ onClick: onClickOpenEditor, pageType: bettingHistoryPageType })
  }

  const onClickWritePick = async (): Promise<void> => {
    if (!authStore.isSignedIn) {
      authStore.showLoginPopup(<Login/>)
      return
    }

    switch (fromView) {
      case FROM_VIEW_TYPE.ADV_HOME:
        logStore.writeButtonLog({ pageId: 'a010', buttonId: '006' }).catch(console.error)
        break
      case FROM_VIEW_TYPE.USER_PICK_MATCH_LIST:
        logStore.writeButtonLog({ pageId: 'a040', buttonId: '004' }).catch(console.error)
        break
    }

    if (pageType === PAGE_TYPE.USER_PICK_DETAIL && selectedUserPickMatch) {
      if (Utils.isStartedMatch(selectedUserPickMatch.dateOfMatch)) {
        Utils.showToast(TOAST_TYPE.INFO, '경기 시작 시간이 초과 되었습니다.')
        return
      }
    }

    const penaltyCheckResult = await penaltyStore.checkUserPenalty(USER_PENALTY_TYPE.USER_PICK)
    if (_.isEmpty(penaltyCheckResult)) {
      const item = StorageUtils.isShowUserPickFirstInfo()
      if (item) {
        showBettingHistory()
      } else {
        if (onGoingBetCount === 0) {
          StorageUtils.saveShowUserPickFirstInfo()
          DialogUtils.showUserPickNullOngoingBetInfo()
        } else {
          DialogUtils.showUserPickIsOngoingBetInfo({ closeCallback: showBettingHistory })
        }
      }
    } else {
      DialogUtils.showPenaltyInfo(penaltyCheckResult)
    }
  }

  return (
    <UserPickWriteBtn fixed={fixed} sticky={sticky} float={float} className={className}>
      <AdvButton
        flex
        alignCenter
        width={44}
        height={44}
        borderRadius={'50%'}
        ico={<WriteMessageIco/>}
        icoSize={30}
        onClick={onClickWritePick}
        className="write-btn"
      />

      {showBanner && <UserPickPromotionBanner/>}
    </UserPickWriteBtn>
  )
})
